import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Checkbox,
  Divider,
  FormControl,
  InputLabel,
  Input,
  InputAdornment,
  IconButton,
  Typography,
  Slider,
  Paper,
  MenuItem,
  Select,
} from "@mui/material";

import SearchIcon from "@mui/icons-material/Search";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import ClearIcon from "@mui/icons-material/Clear";
import CircleIcon from "@mui/icons-material/Circle";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useTheme } from "@mui/material/styles";
import axios from "axios";
import { useNavigate } from "react-router-dom";

const ActorJobTitles = [
  "Acting",
  "Commercials",
  "Content Creator",
  "Crew",
  "Dancing",
  "Modeling",
  "Reality TV",
  "TV Shows",
  "Voiceover",
];

function ageText(value) {
  return `${value}`;
}

function weightText(value) {
  return `${value}`;
}

const ageMarks = [
  {
    value: 18,
    label: "18",
  },
  {
    value: 100,
    label: "100",
  },
];

const heightMarks = [
  { value: 0, label: "0' 0\"" },
  { value: 24, label: "2' 0\"" },
  { value: 48, label: "4' 0\"" },
  { value: 72, label: "6' 0\"" },
  { value: 96, label: "8' 0\"" },
];

const heightText = (value) => {
  const feet = Math.floor(value / 12);
  const inches = value % 12;
  return `${feet}' ${inches}"`;
};

const weightMarks = [
  {
    value: 0,
    label: "0",
  },
  {
    value: 100,
    label: "100",
  },
  {
    value: 200,
    label: "200",
  },
  {
    value: 300,
    label: "300",
  },
  {
    value: 400,
    label: "400",
  },
  {
    value: 500,
    label: "500",
  },
];

const hairColors = [
  {
    value: "black",
    color: "#000",
  },
  {
    value: "brown",
    color: "#592d06",
  },
  {
    value: "red",
    color: "#de1102",
  },
  {
    value: "blonde",
    color: "#FFB400",
  },
  {
    value: "gray",
    color: "#97928B",
  },
];

const eyeColors = [
  {
    value: "black",
    color: "#000",
  },
  {
    value: "brown",
    color: "#592d06",
  },
  {
    value: "blue",
    color: "#1569C7",
  },
  {
    value: "green",
    color: "#19A337",
  },
  {
    value: "gray",
    color: "#97928B",
  },
];

const ethnicities = [
  {
    value: "black",
    label: "African American",
  },
  {
    value: "asian",
    label: "Asian",
  },
  {
    value: "latino/hispanic",
    label: "Latino  Hispanic",
  },
  {
    value: "white/caucasian",
    label: "European American",
  },
  {
    value: "middle_eastern",
    label: "Middle Eastern",
  },
  {
    value: "native_american",
    label: "Native American",
  },
  {
    value: "pacific_islander",
    label: "Pacific Islander",
  },
  {
    value: "indian/south_asian",
    label: "Indian  South Asian",
  },
  {
    value: "fba",
    label: "FBA",
  },
  {
    value: "other",
    label: "Other",
  },
];
const Genders = ["Male", "Female", "Non-Binary", "Transgender"];

const ActorSearchFilter = ({
  searchInputs,
  setSearchInputs,
  handleSearch,
  ageValue,
  setAgeValue,
  heightValue,
  setHeightValue,
  weightValue,
  setWeightValue,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();

  const [addressContent, setAddressContent] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [searchRadius, setSearchRadius] = useState(10);
  const [error, setError] = useState("");
  const debounceTimeoutRef = useRef(null);

  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 3958.8; // Earth's radius in miles
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLon = ((lon2 - lon1) * Math.PI) / 180;

    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distance = R * c;

    return distance;
  };

  const parseHeight = (height) => {
    if (!height) return 0;

    if (typeof height === "string" && height.includes("ft")) {
      const [feet, inches] = height.split(" ");
      const feetValue = parseInt(feet.replace("ft", ""), 10);
      const inchesValue = parseInt(inches.replace("in", ""), 10);
      return feetValue * 12 + inchesValue;
    }

    return parseFloat(height);
  };

  const handleInputChange = (name, value) => {
    setSearchInputs((prevDetails) => ({
      ...prevDetails,
      [name]: value,
    }));
  };

  const handleJobTitleChange = (event) => {
    const value = event.target.value;
    setSearchInputs((prevDetails) => ({
      ...prevDetails,
      jobTitles: value,
    }));
    handleSearch({
      ...searchInputs,
      jobTitles: value,
    });
    navigate(`/castingcalls?jobTitle=${encodeURIComponent(value)}`);
  };

  const handleCheckboxChange = (field, value) => (e) => {
    const newValues = e.target.checked
      ? [...(searchInputs[field] || []), value]
      : (searchInputs[field] || []).filter((v) => v !== value);

    setSearchInputs((prevDetails) => ({
      ...prevDetails,
      [field]: newValues,
    }));
    handleSearch({
      ...searchInputs,
      [field]: newValues,
    });
  };

  const handleAgeChange = (event, newValue) => {
    setAgeValue(newValue);
    setSearchInputs((prev) => ({ ...prev, age: newValue }));
    handleSearch({ ...searchInputs, age: newValue });
  };

  const handleHeightChange = (event, newValue) => {
    setHeightValue(newValue);
    setSearchInputs((prev) => ({ ...prev, height: newValue }));
    handleSearch({ ...searchInputs, height: newValue });
  };

  const handleWeightChange = (event, newValue) => {
    setWeightValue(newValue);
    setSearchInputs((prev) => ({ ...prev, weight: newValue }));
    handleSearch({ ...searchInputs, weight: newValue });
  };

  const fetchSuggestions = async (query) => {
    try {
      const response = await axios.get(
        "https://nominatim.openstreetmap.org/search",
        {
          params: {
            q: query,
            format: "json",
            addressdetails: 1,
            limit: 5,
            countrycodes: "us",
          },
        },
      );
      setSuggestions(response.data);
    } catch (error) {
      console.error("Error fetching suggestions:", error);
      setSuggestions([]);
    }
  };

  const handleAddressChange = (e) => {
    const input = e.target.value;
    setAddressContent(input);

    if (!input || input.length === 0) {
      setSelectedAddress(null);
      setSuggestions([]);
      const newSearchInputs = { ...searchInputs };
      delete newSearchInputs.location;
      delete newSearchInputs.radius;
      setSearchInputs(newSearchInputs);
      handleSearch(newSearchInputs);
      return;
    }

    setSelectedAddress(null);
    setError("");

    if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current);

    if (input.length > 2) {
      debounceTimeoutRef.current = setTimeout(() => {
        fetchSuggestions(input);
      }, 300);
    } else {
      setSuggestions([]);
    }
  };

  const handleSelectAddress = (address) => {
    setSelectedAddress(address);
    setAddressContent(address.display_name);
    setSuggestions([]);

    const newSearchInputs = {
      ...searchInputs,
      location: address,
      radius: searchRadius,
    };
    setSearchInputs(newSearchInputs);
    handleSearch(newSearchInputs);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "20px",
        background: "#f8f9fa",
        padding: "20px",
        borderRadius: "10px",
        boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
      }}
    >
      <Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Search & Filters
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <FormControl sx={{ mb: 2 }} fullWidth variant="filled">
          <InputLabel htmlFor="search-keyword-input">Search by Name</InputLabel>
          <Input
            id="search-keyword-input"
            value={searchInputs.name}
            onChange={(e) => {
              handleInputChange("name", e.target.value);
              handleSearch({ ...searchInputs, name: e.target.value });
            }}
            endAdornment={
              <InputAdornment position="end">
                <IconButton onClick={() => handleSearch(searchInputs)}>
                  <SearchIcon />
                </IconButton>
              </InputAdornment>
            }
          />
        </FormControl>

        {/* Location Search */}
        <FormControl sx={{ mb: 0 }} fullWidth variant="filled">
          <InputLabel htmlFor="location-search">Search by Location</InputLabel>
          <Input
            id="location-search"
            value={addressContent}
            onChange={handleAddressChange}
            endAdornment={
              <InputAdornment position="end">
                {addressContent && (
                  <IconButton
                    onClick={() =>
                      handleAddressChange({ target: { value: "" } })
                    }
                    size="small"
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                )}
                <IconButton>
                  <LocationOnIcon />
                </IconButton>
              </InputAdornment>
            }
          />
          {suggestions.length > 0 && (
            <Paper
              sx={{
                position: "absolute",
                zIndex: 1000,
                mt: "60px",
                width: "100%",
                maxHeight: "200px",
                overflow: "auto",
              }}
            >
              {suggestions.map((suggestion) => (
                <MenuItem
                  key={suggestion.place_id}
                  onClick={() => handleSelectAddress(suggestion)}
                >
                  {suggestion.display_name}
                </MenuItem>
              ))}
            </Paper>
          )}
        </FormControl>

        {selectedAddress && (
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <FormControl sx={{ width: "10ch" }} variant="standard">
              <InputLabel htmlFor="radius-input">Radius (mi)</InputLabel>
              <Input
                id="radius-input"
                type="number"
                value={searchRadius}
                onChange={(e) => setSearchRadius(e.target.value)}
                onBlur={() => {
                  if (selectedAddress && searchRadius) {
                    const newSearchInputs = {
                      ...searchInputs,
                      location: selectedAddress,
                      radius: searchRadius,
                    };
                    setSearchInputs(newSearchInputs);
                    handleSearch(newSearchInputs);
                  }
                }}
              />
            </FormControl>
          </Box>
        )}
      </Box>

      <Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Job Titles
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <FormControl fullWidth variant="filled">
          <InputLabel id="actor-job-titles-label">Job Title</InputLabel>
          <Select
            labelId="actor-job-titles-label"
            id="actor-job-titles-select"
            value={searchInputs.jobTitles || ""}
            onChange={handleJobTitleChange}
            MenuProps={{
              PaperProps: {
                style: {
                  maxHeight: 250,
                  width: 250,
                },
              },
            }}
          >
            <MenuItem value="">Any</MenuItem>
            {ActorJobTitles.map((jobTitle) => (
              <MenuItem key={jobTitle} value={jobTitle}>
                {jobTitle}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Hair Color
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: "10px",
          }}
        >
          {hairColors.map((color, index) => (
            <Box
              key={index}
              sx={{
                display: "flex",
                alignItems: "center",
                color: color.color,
              }}
            >
              <Checkbox
                sx={{
                  color: color.color,
                  "&.Mui-checked": { color: `${color.color}` },
                  padding: 0,
                  border: "1px solid black",
                }}
                icon={<CircleIcon />}
                checkedIcon={<CheckCircleIcon />}
                checked={searchInputs.hairColor.includes(color.value)}
                onChange={handleCheckboxChange("hairColor", color.value)}
              />
            </Box>
          ))}
        </Box>
      </Box>

      <Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Eye Color
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: "10px",
          }}
        >
          {eyeColors.map((color, index) => (
            <Box
              key={index}
              sx={{
                display: "flex",
                alignItems: "center",
                color: color.color,
              }}
            >
              <Checkbox
                sx={{
                  color: `${color.color}`,
                  "&.Mui-checked": { color: `${color.color}` },
                  padding: 0,
                  border: "1px solid black",
                }}
                icon={<CircleIcon />}
                checkedIcon={<CheckCircleIcon />}
                checked={searchInputs.eyeColor.includes(color.value)}
                onChange={handleCheckboxChange("eyeColor", color.value)}
              />
            </Box>
          ))}
        </Box>
      </Box>

      <Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Gender
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            flexWrap: "wrap",
            gap: "10px",
          }}
        >
          {Genders.map((gender) => (
            <Box key={gender} sx={{ display: "flex", alignItems: "center" }}>
              <Checkbox
                sx={{ padding: 0, marginRight: "5px" }}
                color="secondary"
                checked={searchInputs.gender.includes(gender.toLowerCase())}
                onChange={handleCheckboxChange("gender", gender.toLowerCase())}
              />
              <Typography variant="caption">{gender}</Typography>
            </Box>
          ))}
        </Box>
      </Box>

      <Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Age (Years)
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Slider
          getAriaLabel={() => "Age Range"}
          value={ageValue}
          onChange={handleAgeChange}
          valueLabelDisplay="auto"
          getAriaValueText={ageText}
          marks={ageMarks}
          sx={{
            marginLeft: "10px",
            marginTop: "10px",
            width: "200px",
            color: theme.palette.secondary.main,
            "& .MuiSlider-rail": {
              backgroundColor: "#e0e0e0",
              opacity: 1,
            },
            "& .MuiSlider-track": {
              background: `linear-gradient(to bottom, ${theme.palette.secondary.main}, ${theme.palette.primary.main})`,
              border: "none",
            },
            "& .MuiSlider-thumb": {
              width: 20,
              height: 20,
              backgroundColor: theme.palette.background.paper,
              border: `2px solid ${theme.palette.secondary.main}`,
              boxShadow: "0px 2px 6px rgba(0, 0, 0, 0.3)",
              "&:hover, &.Mui-focusVisible": {
                boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.4)",
              },
            },
            "& .MuiSlider-mark": {
              backgroundColor: "#bfbfbf",
              height: 8,
              width: 2,
              "&.MuiSlider-markActive": {
                backgroundColor: theme.palette.secondary.main,
              },
            },
            "& .MuiSlider-markLabel": {
              transform: "translateX(-5px)",
              color: theme.palette.text.secondary,
              fontSize: "0.875rem",
              fontWeight: 500,
            },
            "& .MuiSlider-valueLabel": {
              backgroundColor: theme.palette.secondary.main,
              color: theme.palette.background.paper,
              borderRadius: "8px",
              padding: "4px 8px",
              fontSize: "0.875rem",
              fontWeight: 600,
              boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.2)",
              "&:before": {
                display: "none",
              },
            },
          }}
          color="secondary"
        />
      </Box>

      <Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Height (ft/in)
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Box
          sx={{
            marginTop: "20px",
            marginLeft: "10px",
          }}
        >
          <Slider
            orientation="horizontal"
            getAriaLabel={() => "Height Range"}
            value={heightValue}
            onChange={handleHeightChange}
            valueLabelDisplay="auto"
            valueLabelFormat={heightText}
            marks={heightMarks}
            min={0}
            max={96}
            sx={{
              width: "200px",
              color: theme.palette.secondary.main,
              "& .MuiSlider-rail": {
                backgroundColor: "#e0e0e0",
                opacity: 1,
              },
              "& .MuiSlider-track": {
                background: `linear-gradient(to bottom, ${theme.palette.secondary.main}, ${theme.palette.primary.main})`,
                border: "none",
              },
              "& .MuiSlider-thumb": {
                width: 20,
                height: 20,
                backgroundColor: theme.palette.background.paper,
                border: `2px solid ${theme.palette.secondary.main}`,
                boxShadow: "0px 2px 6px rgba(0, 0, 0, 0.3)",
                "&:hover, &.Mui-focusVisible": {
                  boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.4)",
                },
              },
              "& .MuiSlider-mark": {
                backgroundColor: "#bfbfbf",
                height: 8,
                width: 2,
                "&.MuiSlider-markActive": {
                  backgroundColor: theme.palette.secondary.main,
                },
              },
              "& .MuiSlider-markLabel": {
                transform: "translateX(-5px)",
                color: theme.palette.text.secondary,
                fontSize: "0.875rem",
                fontWeight: 500,
              },
              "& .MuiSlider-valueLabel": {
                backgroundColor: theme.palette.secondary.main,
                color: theme.palette.background.paper,
                borderRadius: "8px",
                padding: "4px 8px",
                fontSize: "0.875rem",
                fontWeight: 600,
                boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.2)",
                "&:before": {
                  display: "none",
                },
              },
            }}
            color="secondary"
          />
        </Box>
      </Box>

      <Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Weight (lbs)
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Slider
          getAriaLabel={() => "Weight Range"}
          value={weightValue}
          onChange={handleWeightChange}
          valueLabelDisplay="auto"
          getAriaValueText={weightText}
          marks={weightMarks}
          min={0}
          max={500}
          sx={{
            marginLeft: "10px",
            width: "200px",
            color: theme.palette.secondary.main,
            "& .MuiSlider-rail": {
              backgroundColor: "#e0e0e0",
              opacity: 1,
            },
            "& .MuiSlider-track": {
              background: `linear-gradient(to bottom, ${theme.palette.secondary.main}, ${theme.palette.primary.main})`,
              border: "none",
            },
            "& .MuiSlider-thumb": {
              width: 20,
              height: 20,
              backgroundColor: theme.palette.background.paper,
              border: `2px solid ${theme.palette.secondary.main}`,
              boxShadow: "0px 2px 6px rgba(0, 0, 0, 0.3)",
              "&:hover, &.Mui-focusVisible": {
                boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.4)",
              },
            },
            "& .MuiSlider-mark": {
              backgroundColor: "#bfbfbf",
              height: 8,
              width: 2,
              "&.MuiSlider-markActive": {
                backgroundColor: theme.palette.secondary.main,
              },
            },
            "& .MuiSlider-markLabel": {
              transform: "translateX(-5px)",
              color: theme.palette.text.secondary,
              fontSize: "0.875rem",
              fontWeight: 500,
            },
            "& .MuiSlider-valueLabel": {
              backgroundColor: theme.palette.secondary.main,
              color: theme.palette.background.paper,
              borderRadius: "8px",
              padding: "4px 8px",
              fontSize: "0.875rem",
              fontWeight: 600,
              boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.2)",
              "&:before": {
                display: "none",
              },
            },
          }}
          color="secondary"
        />
      </Box>

      <Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Ethnicity
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            flexWrap: "wrap",
            gap: "10px",
          }}
        >
          {ethnicities.map((et) => (
            <Box key={et.value} sx={{ display: "flex", alignItems: "center" }}>
              <Checkbox
                sx={{ padding: 0, marginRight: "5px" }}
                color="secondary"
                checked={searchInputs.ethnicity.includes(et.value)}
                onChange={handleCheckboxChange("ethnicity", et.value)}
              />
              <Typography variant="caption">{et.label}</Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default ActorSearchFilter;
